import { BigQueryEditForm } from '@/components/admin/data-sources/BigQueryEditForm';
import { DataSourceForm } from '@/components/admin/data-sources/DataSourceForm';
import { ExcelEditForm } from '@/components/admin/data-sources/ExcelEditForm';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import type { CreateDataSourceRequest, UpdateDataSourceRequest } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

interface AdminDataSourceEditProps {
  id: string;
}

export function AdminDataSourceEdit({ id }: AdminDataSourceEditProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const dataSourceQuery = useQuery({
    queryKey: ['admin', 'data-sources', id],
    queryFn: () => api.dataSourceGet(id),
  });

  const mutation = useMutation({
    mutationFn: (body: UpdateDataSourceRequest) => api.dataSourceUpdate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] });
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources', id] });
      navigate({ to: '/admin/data-sources/$id', params: { id } });
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '데이터소스 저장에 실패했습니다.');
    },
  });

  const ds = dataSourceQuery.data;

  if (dataSourceQuery.isLoading) {
    return (
      <PageShell>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (dataSourceQuery.error || !ds) {
    return (
      <PageShell header={<BackLink id={id} />}>
        <p className="text-sm text-destructive">
          {dataSourceQuery.error instanceof ApiError
            ? dataSourceQuery.error.message
            : '데이터소스를 찾을 수 없습니다.'}
        </p>
      </PageShell>
    );
  }

  const initial: Partial<CreateDataSourceRequest> = {
    name: ds.name,
    kind: ds.kind,
    host: ds.host,
    port: ds.port,
    database: ds.database,
    username: ds.username,
    password: '',
    sslMode: ds.sslMode,
    schemaFilter: ds.schemaFilter,
    extraConfig: ds.extraConfig,
    description: ds.description,
  };

  const isExcel = ds.kind === 'excel';
  const isBigQuery = ds.kind === 'bigquery';

  return (
    <PageShell
      header={
        <>
          <BackLink id={id} />
          <PageHeader
            title="데이터소스 편집"
            description={
              isExcel
                ? 'Excel·CSV 데이터소스 — 이름과 설명만 편집할 수 있습니다.'
                : '이름·호스트·계정·비밀번호·설명 등을 수정할 수 있습니다. DB 종류는 변경할 수 없습니다.'
            }
          />
        </>
      }
    >
      <div className="max-w-2xl">
        {serverError ? (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {serverError}
          </div>
        ) : null}

        {isExcel ? (
          <ExcelEditForm
            dataSource={ds}
            submitting={mutation.isPending}
            onSubmit={(body) => {
              setServerError(null);
              mutation.mutate(body);
            }}
            onCancel={() => navigate({ to: '/admin/data-sources/$id', params: { id } })}
          />
        ) : isBigQuery ? (
          <BigQueryEditForm
            dataSource={ds}
            submitting={mutation.isPending}
            onSubmit={(body) => {
              setServerError(null);
              mutation.mutate(body);
            }}
            onCancel={() => navigate({ to: '/admin/data-sources/$id', params: { id } })}
          />
        ) : (
          <DataSourceForm
            mode="edit"
            dataSourceId={id}
            kindLocked
            initial={initial}
            submitLabel="변경사항 저장"
            submitting={mutation.isPending}
            onSubmit={(body) => {
              setServerError(null);
              mutation.mutate(body as UpdateDataSourceRequest);
            }}
            onCancel={() => navigate({ to: '/admin/data-sources/$id', params: { id } })}
          />
        )}
      </div>
    </PageShell>
  );
}

function BackLink({ id }: { id: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/admin/data-sources/$id', params: { id } })}
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      데이터소스 상세로
    </button>
  );
}
